// Central source of truth for internal route links (SEO).
// Pure data + helpers — safe to import from both server and client components.

export interface RouteLink {
  slug: string;
  label: string;
}

export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

/** Turn a URL segment back into a display name: "delhi" → "Delhi". */
export const humanize = (segment: string): string =>
  segment
    .replace(/-/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

export const routeSlug = (from: string, to: string): string =>
  `${slugify(from)}-to-${slugify(to)}`;

export const routeLabel = (from: string, to: string): string => `${from} to ${to}`;

// Major Haryana Roadways hub cities — used to generate hub pages and the
// internal link graph (origin/destination clusters).
export const MAJOR_CITIES = [
  "Delhi",
  "Chandigarh",
  "Hisar",
  "Rohtak",
  "Faridabad",
  "Gurgaon",
  "Ambala",
  "Panchkula",
  "Karnal",
  "Panipat",
  "Sonipat",
  "Sirsa",
  "Bhiwani",
  "Jind",
  "Kaithal",
  "Yamunanagar",
  "Kurukshetra",
  "Rewari",
];

// Hand-picked high-traffic city pairs to seed the index and homepage grid.
export const POPULAR_ROUTE_PAIRS: Array<[string, string]> = [
  ["Chandigarh", "Delhi"],
  ["Delhi", "Chandigarh"],
  ["Chandigarh", "Hisar"],
  ["Hisar", "Chandigarh"],
  ["Delhi", "Rohtak"],
  ["Rohtak", "Delhi"],
  ["Delhi", "Faridabad"],
  ["Faridabad", "Delhi"],
  ["Delhi", "Gurgaon"],
  ["Gurgaon", "Delhi"],
  ["Chandigarh", "Ambala"],
  ["Ambala", "Chandigarh"],
  ["Chandigarh", "Panchkula"],
  ["Rohtak", "Hisar"],
  ["Hisar", "Rohtak"],
  ["Delhi", "Karnal"],
];

export const popularRouteLinks = (): RouteLink[] =>
  POPULAR_ROUTE_PAIRS.map(([from, to]) => ({
    slug: routeSlug(from, to),
    label: routeLabel(from, to),
  }));

/** Resolve a single-segment slug to a known city name, or null. */
export const cityFromSlug = (slug: string): string | null => {
  const target = slug.toLowerCase();
  return MAJOR_CITIES.find((c) => slugify(c) === target) ?? null;
};

export const isCitySlug = (slug: string): boolean => cityFromSlug(slug) !== null;

export const cityHubLinks = (): RouteLink[] =>
  MAJOR_CITIES.map((c) => ({ slug: slugify(c), label: c }));

/** All routes departing a city to every other major city. */
export const cityRouteLinks = (city: string, limit?: number): RouteLink[] => {
  const links = MAJOR_CITIES.filter((c) => c.toLowerCase() !== city.toLowerCase()).map((c) => ({
    slug: routeSlug(city, c),
    label: routeLabel(city, c),
  }));
  return typeof limit === "number" ? links.slice(0, limit) : links;
};

export interface RelatedRouteGroups {
  reverse: RouteLink;
  fromOrigin: RouteLink[];
  toDestination: RouteLink[];
}

/** Contextual links for a route page: reverse, same-origin, same-destination. */
export const relatedRoutes = (from: string, to: string, perGroup = 6): RelatedRouteGroups => {
  const exclude = [from.toLowerCase(), to.toLowerCase()];
  const others = MAJOR_CITIES.filter((c) => !exclude.includes(c.toLowerCase()));
  return {
    reverse: { slug: routeSlug(to, from), label: routeLabel(to, from) },
    fromOrigin: others
      .slice(0, perGroup)
      .map((c) => ({ slug: routeSlug(from, c), label: routeLabel(from, c) })),
    toDestination: others
      .slice(0, perGroup)
      .map((c) => ({ slug: routeSlug(c, to), label: routeLabel(c, to) })),
  };
};
